import { useField } from "formik";

import { Label } from "components/ui/label";

import {
  MultiSelect,
  type MultiSelectOption,
  formatMultiSelectLabel,
} from "./multi-select";

const SEARCHABLE_THRESHOLD = 8;

export interface DefinitionItem {
  displayName?: string;
  value?: string | number;
  [key: string]: unknown;
}

export function toMultiSelectOptions(
  items: ReadonlyArray<DefinitionItem | string>,
  getLabel?: (item: any) => string,
): MultiSelectOption<string>[] {
  return items.map((item) => {
    if (typeof item === "string") return { value: item, label: item };
    const value =
      item.value !== undefined
        ? String(item.value)
        : String(item.displayName ?? "");
    const label = getLabel ? getLabel(item) : (item.displayName ?? value);
    return { value, label };
  });
}

export interface FormikMultiSelectProps {
  id: string;
  label: string;
  options: MultiSelectOption<string>[];
  fieldName: string;
  handleSubmit: () => void;
  emptyLabel?: string;
}

/**
 * Formik compatibility wrapper around {@link MultiSelect}. The filtering is
 * performed by the MultiSelect itself; this binds it to a Formik field and
 * submits the form on change.
 */
export function FormikMultiSelect({
  id,
  label,
  options,
  fieldName,
  handleSubmit,
  emptyLabel,
}: FormikMultiSelectProps) {
  const [field, , helpers] = useField<string[]>(fieldName);
  const selectedLabels = options
    .filter((o) => field.value.includes(o.value))
    .map((o) => o.label);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <Label htmlFor={id}>{label}</Label>
      <MultiSelect
        id={id}
        options={options}
        value={field.value}
        onValueChange={(next) => {
          helpers.setValue(next);
          handleSubmit();
        }}
        triggerLabel={formatMultiSelectLabel(
          selectedLabels,
          emptyLabel ?? label,
        )}
        searchable={options.length > SEARCHABLE_THRESHOLD}
      />
    </div>
  );
}
